import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Clock,
  DollarSign,
  Activity,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, change, changeType, icon: Icon, color, loading }) => {
  const isPositive = changeType === 'positive';
  const isNegative = changeType === 'negative';
  
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{title}</p>
            {loading ? (
              <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mt-2" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
            )}
            {change !== undefined && !loading && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${
                isPositive ? 'text-emerald-600' : isNegative ? 'text-red-500' : 'text-slate-500'
              }`}>
                {isPositive ? <ArrowUp className="w-4 h-4" /> : isNegative ? <ArrowDown className="w-4 h-4" /> : null}
                <span>{change}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RealTimeStats = ({ userRole = 'professional' }) => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const endpoint = userRole === 'professional' 
        ? '/api/professionals/stats/dashboard'
        : '/api/admin/stats/dashboard';
        
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token, userRole]);

  useEffect(() => {
    fetchStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const professionalStats = [
    {
      title: "RDV Aujourd'hui",
      value: stats?.appointments_today ?? '-',
      change: stats?.appointments_change ? `${stats.appointments_change > 0 ? '+' : ''}${stats.appointments_change}%` : undefined,
      changeType: stats?.appointments_change > 0 ? 'positive' : stats?.appointments_change < 0 ? 'negative' : 'neutral',
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      title: "Patients en attente",
      value: stats?.waiting_patients ?? '-',
      icon: Users,
      color: 'bg-amber-500'
    },
    {
      title: "Revenus (mois)",
      value: stats?.monthly_revenue ? `${stats.monthly_revenue.toLocaleString()}€` : '-',
      change: stats?.revenue_change ? `${stats.revenue_change > 0 ? '+' : ''}${stats.revenue_change}%` : undefined,
      changeType: stats?.revenue_change > 0 ? 'positive' : stats?.revenue_change < 0 ? 'negative' : 'neutral',
      icon: DollarSign,
      color: 'bg-emerald-500'
    },
    {
      title: "Taux occupation",
      value: stats?.occupancy_rate ? `${stats.occupancy_rate}%` : '-',
      icon: Activity,
      color: 'bg-purple-500'
    }
  ];

  const adminStats = [
    {
      title: "Utilisateurs actifs",
      value: stats?.active_users ?? '-',
      change: stats?.users_change ? `${stats.users_change > 0 ? '+' : ''}${stats.users_change}%` : undefined,
      changeType: stats?.users_change > 0 ? 'positive' : 'neutral',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: "RDV Aujourd'hui",
      value: stats?.total_appointments_today ?? '-',
      icon: Calendar,
      color: 'bg-emerald-500'
    },
    {
      title: "Revenus (mois)",
      value: stats?.platform_revenue ? `${stats.platform_revenue.toLocaleString()}€` : '-',
      change: stats?.revenue_change ? `${stats.revenue_change > 0 ? '+' : ''}${stats.revenue_change}%` : undefined,
      changeType: stats?.revenue_change > 0 ? 'positive' : stats?.revenue_change < 0 ? 'negative' : 'neutral',
      icon: DollarSign,
      color: 'bg-amber-500'
    },
    {
      title: "Nouveaux inscrits",
      value: stats?.new_registrations ?? '-',
      icon: TrendingUp,
      color: 'bg-purple-500'
    }
  ];

  const displayStats = userRole === 'professional' ? professionalStats : adminStats;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Statistiques en temps réel
        </h3>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-slate-500">
              Mis à jour à {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchStats}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {displayStats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>
    </div>
  );
};

export default RealTimeStats;
